import React, {useCallback} from 'react';
import {useQuickAddContext} from 'components/customer/Product/QuickAdd';
import styled, {css, keyframes} from 'styled-components';
import {Icon} from 'shared/helpers';
import {useMouseOver} from 'shared/helpers/useMouseOver';
import {Content, Popover, Trigger} from '@cabinetsbycomputer/popover';
import {Controls} from 'components/customer/Product/QuickAdd/Controls';
import {lightenColor} from 'components/customer/Confirmation/helpers/lightenColor';
import {Spinner} from 'react-bootstrap';

export const Overlay = () => {
    const {
        addProduct,
        loading,
        isSuccess,
        onClickHandler,
        popoverOpen,
        setPopoverOpen,
        validationMessage,
    } = useQuickAddContext();
    const {isMouseOverComponent, handleMouseEnter, handleMouseExit} =
        useMouseOver();

    const handleQuickAddButtonClick = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            if (popoverOpen) {
                event.preventDefault();

                if (!loading) {
                    void addProduct?.();
                }
            }
        },
        [popoverOpen, addProduct, loading]
    );

    return (
        <Container>
            <CustomizeButton
                onClick={onClickHandler}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseExit}>
                <Icon
                    color={isMouseOverComponent ? 'secondary' : 'primary'}
                    iconName="Options-Edit-Primary.svg"
                />
                <label>Customise</label>
            </CustomizeButton>
            <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
                <QuickAddButton
                    $isSuccess={isSuccess ?? false}
                    $hasError={validationMessage != ''}
                    onClick={handleQuickAddButtonClick}>
                    {loading ? (
                        <Spinner animation="border" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </Spinner>
                    ) : (
                        <Icon
                            iconName={
                                isSuccess
                                    ? 'Button-Tick.svg'
                                    : 'Header-Cart.svg'
                            }
                        />
                    )}

                    <label>{isSuccess ? 'Added' : 'Quick Add'}</label>
                </QuickAddButton>
                <ContentContainer
                    container={document.querySelector('#products-container')}
                    zIndex={11}
                    sideOffset={12}
                    collisionPadding={20}>
                    <Controls />
                </ContentContainer>
            </Popover>
        </Container>
    );
};

export const Button = css`
    border-radius: 16px;
    font-size: 0.8em;
    font-weight: 500;
    padding: 2px 2px;
    display: flex;
    justify-content: space-between;
    align-items: center;

    > label {
        cursor: pointer;
        flex: 1;
    }

    > img {
        width: 22px;
        margin-right: 6px;
    }
`;

const CustomizeButton = styled.button`
    ${Button}
    background: white;
    border: 2px solid ${({theme}) => theme.colors.primary?.main};
    color: ${({theme}) => theme.colors.primary?.main};

    &:hover,
    &:focus,
    &:active {
        border-color: ${({theme}) => theme.colors.secondary?.main};
        color: ${({theme}) => theme.colors.secondary?.main};
    }

    transition:
        color 500ms ease-in-out,
        background 500ms ease-in-out,
        border-color 500ms ease-in-out;
`;

const background = (color?: string, hover?: string) => css`
    background: ${color};

    &:hover,
    &:focus,
    &:active {
        background: ${hover ? hover : color};
    }
`;

const QuickAddButton = styled(Trigger)<{
    $isSuccess: boolean;
    $hasError: boolean;
}>`
    ${Button}
    ${({theme}) => background(theme.colors.secondary?.main, theme.colors.primary?.main)}

    border: 0;
    padding: 4px;
    color: white;
    transition:
        background 500ms ease-in-out,
        border-color 500ms ease-in-out;

    ${({$isSuccess}) => {
        if ($isSuccess) {
            return background('#198754');
        }
    }}
    ${({$hasError}) => {
        if ($hasError) {
            return background('#dc3545');
        }
    }}

    > div {
        width: 22px;
        height: 22px;
        border-width: 3px;
    }
`;

const contentEnter = keyframes`
    from {
        opacity: 0;
        transform: translateY(-8px) scale(0.98);
    }
    to {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
`;

const contentExit = keyframes`
    from {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
    to {
        opacity: 0;
        transform: translateY(-8px) scale(0.98);
    }
`;

const Container = styled.div`
    display: flex;
    flex-direction: column;
    overflow: clip;
    cursor: pointer;
    gap: 10px;
`;

const ContentContainer = styled(Content)`
    background: ${({theme}) => lightenColor(theme.colors.secondary?.main, 85)};
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 10px 15px 15px;
    box-sizing: border-box;
    max-width: calc(var(--radix-popper-anchor-width) + 41px);
    border: 1px solid ${({theme}) => theme.colors.secondary?.main};
    border-radius: 10px;
    box-shadow:
        0 8px 24px rgba(0, 0, 0, 0.16),
        0 2px 6px rgba(0, 0, 0, 0.08);

    .qa-popover-arrow {
        width: 16px;
        height: 8px;
        fill: ${({theme}) => theme.colors.secondary?.main};
    }

    &[data-state='open'] {
        animation: ${contentEnter} 220ms cubic-bezier(0.16, 1, 0.3, 1) forwards;
    }

    &[data-state='closed'] {
        animation: ${contentExit} 140ms ease-in forwards;
    }

    &[data-side='top'] {
        box-shadow:
            0 -4px 12px rgba(0, 0, 0, 0.1),
            0 -10px 28px rgba(0, 0, 0, 0.14);
    }
`;
